import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAtom,
  faSearch,
  faFilter,
  faPlay,
  faChevronDown,
  faChevronUp,
  faSync,
  faDownload,
  faUpload,
  faRobot,
  faTimes,
  faCheck,
  faSpinner,
  faCode,
  faCopy,
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
  faList,
  faDesktop,
  faServer,
  faLaptop,
  faCrosshairs,
  faSave,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { faWindows, faLinux, faApple } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'react-toastify';
import atomicService from '../services/atomicService';
import agentService from '../services/agentService';
import targetService from '../services/targetService';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { AtomicVariablesProvider, useAtomicVariables } from '../contexts/AtomicVariablesContext';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';

// Inner component that uses the context
const AtomicTestLibraryInner = ({ projectId }) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get variable persistence from context
  const {
    getVariable,
    setVariable,
    setMultipleVariables,
    getAllVariables,
    getVariablesWithDefaults,
    getVariableCount,
    clearVariables
  } = useAtomicVariables();

  // Data state
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });

  // Filter state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [platformFilter, setPlatformFilter] = useState(searchParams.get('platform') || '');
  const [executorFilter, setExecutorFilter] = useState(searchParams.get('executor') || '');
  const [tacticFilter, setTacticFilter] = useState(searchParams.get('tactic') || '');
  const [showFilters, setShowFilters] = useState(false);

  // UI state
  const [expandedTest, setExpandedTest] = useState(null);
  const [selectedTests, setSelectedTests] = useState([]);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);

  // Execute modal state
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [executionMode, setExecutionMode] = useState('single'); // single, bulk, chain

  // Fetch data
  const fetchTests = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm || undefined,
        platform: platformFilter || undefined,
        executorType: executorFilter || undefined,
        tactic: tacticFilter || undefined,
        page: pagination.page,
        limit: pagination.limit
      };

      const result = await atomicService.getAtomicTests(filters);
      setTests(result.tests || []);
      setPagination(prev => ({ ...prev, ...result.pagination }));
    } catch (error) {
      toast.error('Failed to load atomic tests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, platformFilter, executorFilter, tacticFilter, pagination.page, pagination.limit]);

  const fetchStats = async () => {
    try {
      const data = await atomicService.getStatsSummary();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const data = await agentService.getAgents({ approved: true });
      setAgents(data.filter(a => a.isApproved));
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const fetchTargets = async () => {
    try {
      // If projectId is provided, fetch project-specific targets, else fetch all
      if (projectId) {
        const data = await targetService.getTargetsByProject(projectId);
        setTargets(data);
      } else {
        const data = await targetService.getTargets();
        setTargets(data);
      }
    } catch (error) {
      console.error('Failed to load targets:', error);
    }
  };

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  useEffect(() => {
    fetchStats();
    fetchAgents();
    fetchTargets();
  }, [projectId]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (platformFilter) params.set('platform', platformFilter);
    if (executorFilter) params.set('executor', executorFilter);
    if (tacticFilter) params.set('tactic', tacticFilter);
    setSearchParams(params);
  }, [searchTerm, platformFilter, executorFilter, tacticFilter, setSearchParams]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle test selection
  const toggleTestSelection = (testId) => {
    setSelectedTests(prev =>
      prev.includes(testId)
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
  };

  // Handle test execution
  const handleExecute = async () => {
    if (!selectedAgent) {
      toast.error('Please select an agent');
      return;
    }

    const testsToExecute = selectedTests.length > 0
      ? selectedTests
      : expandedTest ? [expandedTest] : [];

    if (testsToExecute.length === 0) {
      toast.error('No tests selected');
      return;
    }

    // Save input values to persistent storage for reuse
    if (Object.keys(inputValues).length > 0) {
      setMultipleVariables(inputValues);
    }

    setExecuteLoading(true);
    try {
      const options = {
        inputValues,
        targetIds: selectedTargets,
        project: projectId
      };

      const agentName = agents.find(a => a._id === selectedAgent)?.name || 'agent';

      if (executionMode === 'chain' && testsToExecute.length > 1) {
        const result = await atomicService.executeTestChain(testsToExecute, selectedAgent, {
          ...options,
          delayBetweenTests: 5,
          stopOnError: false
        });
        toast.success(
          <span>Test chain started on <strong>{agentName}</strong>: {result.tasks.length} tests queued. <button className="underline text-blue-300 ml-1" onClick={() => navigate(`/agents/${selectedAgent}?tab=tasks`)}>View Results</button></span>,
          { autoClose: 8000 }
        );
      } else if (testsToExecute.length > 1) {
        for (const testId of testsToExecute) {
          await atomicService.executeTest(testId, selectedAgent, options);
        }
        toast.success(
          <span>{testsToExecute.length} tests queued on <strong>{agentName}</strong>. <button className="underline text-blue-300 ml-1" onClick={() => navigate(`/agents/${selectedAgent}?tab=tasks`)}>View Results</button></span>,
          { autoClose: 8000 }
        );
      } else {
        const result = await atomicService.executeTest(testsToExecute[0], selectedAgent, options);
        toast.success(
          <span>Test sent to <strong>{agentName}</strong>: {result.task.sent ? 'executing' : 'queued'}. <button className="underline text-blue-300 ml-1" onClick={() => navigate(`/agents/${selectedAgent}?tab=tasks`)}>View Results</button></span>,
          { autoClose: 8000 }
        );
      }

      setShowExecuteModal(false);
      setSelectedTests([]);
      setSelectedTargets([]);
      setInputValues({});
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to execute test');
    } finally {
      setExecuteLoading(false);
    }
  };

  // Sync technique from GitHub
  const handleSyncTechnique = async (techniqueId) => {
    try {
      const result = await atomicService.syncTechnique(techniqueId);
      toast.success(`Synced ${techniqueId}: ${result.imported} new, ${result.updated} updated`);
      fetchTests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to sync ${techniqueId}`);
    }
  };

  // Copy command to clipboard
  const copyCommand = (command) => {
    navigator.clipboard.writeText(command);
    toast.success('Command copied to clipboard');
  };

  // Get platform icon
  const getPlatformIcon = (platform, showLabel = true) => {
    const normalizedPlatform = platform.toLowerCase();

    if (normalizedPlatform === 'windows') {
      return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
          darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
        }`}>
          <FontAwesomeIcon icon={faWindows} />
          {showLabel && 'Windows'}
        </span>
      );
    }
    if (normalizedPlatform === 'linux') {
      return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
          darkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
        }`}>
          <FontAwesomeIcon icon={faLinux} />
          {showLabel && 'Linux'}
        </span>
      );
    }
    if (normalizedPlatform === 'darwin' || normalizedPlatform === 'macos') {
      return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
          darkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-700'
        }`}>
          <FontAwesomeIcon icon={faApple} />
          {showLabel && 'macOS'}
        </span>
      );
    }
    return <span className="text-xs px-2 py-1 rounded bg-gray-500/20 text-gray-500">{platform}</span>;
  };

  // Render test card
  const renderTestCard = (test) => {
    const isExpanded = expandedTest === test._id;
    const isSelected = selectedTests.includes(test._id);

    return (
      <div
        key={test._id}
        className={`border rounded-lg overflow-hidden transition-all ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      >
        {/* Header */}
        <div
          className={`p-4 cursor-pointer ${darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}`}
          onClick={() => setExpandedTest(isExpanded ? null : test._id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleTestSelection(test._id);
                }}
                className="mt-1 rounded"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span className={`font-mono text-sm px-2 py-0.5 rounded ${
                    darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                  }`}>
                    {test.techniqueId}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {atomicService.getExecutorIcon(test.executorType)} {test.executorType}
                  </span>
                </div>
                <h3 className={`font-medium mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {test.name}
                </h3>
                <p className={`text-sm mt-1 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {test.description}
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  {test.platforms?.map(p => (
                    <span key={p} className="flex items-center space-x-1">
                      {getPlatformIcon(p)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedTest(test._id);
                  setShowExecuteModal(true);
                }}
                className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                title="Execute test"
              >
                <FontAwesomeIcon icon={faPlay} />
              </button>
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className={darkMode ? 'text-gray-400' : 'text-gray-500'}
              />
            </div>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className={`border-t p-4 ${darkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-50'}`}>
            {/* Command */}
            {test.executorCommand && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FontAwesomeIcon icon={faCode} className="mr-2" />
                    Command
                  </h4>
                  <button
                    onClick={() => copyCommand(test.executorCommand)}
                    className="text-xs text-blue-500 hover:text-blue-600"
                  >
                    <FontAwesomeIcon icon={faCopy} className="mr-1" />
                    Copy
                  </button>
                </div>
                <pre className={`p-3 rounded text-sm overflow-x-auto ${
                  darkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-800 text-gray-100'
                }`}>
                  {test.executorCommand}
                </pre>
              </div>
            )}

            {/* Input Arguments */}
            {test.inputArguments?.length > 0 && (
              <div className="mb-4">
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Input Arguments
                </h4>
                <div className="space-y-2">
                  {test.inputArguments.map(arg => (
                    <div key={arg.name} className={`p-2 rounded ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                      <div className="flex items-center justify-between">
                        <code className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          #{'{' + arg.name + '}'}
                        </code>
                        {arg.default && (
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Default: {arg.default}
                          </span>
                        )}
                      </div>
                      {arg.description && (
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {arg.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cleanup Command */}
            {test.cleanupCommand && (
              <div className="mb-4">
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-yellow-500" />
                  Cleanup Command
                </h4>
                <pre className={`p-3 rounded text-sm overflow-x-auto ${
                  darkMode ? 'bg-yellow-900/20 text-yellow-200' : 'bg-yellow-50 text-yellow-800'
                }`}>
                  {test.cleanupCommand}
                </pre>
              </div>
            )}

            {/* Dependencies */}
            {test.dependencies?.length > 0 && (
              <div className="mb-4">
                <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-500" />
                  Dependencies ({test.dependencies.length})
                </h4>
                <div className="space-y-2">
                  {test.dependencies.map((dep, i) => (
                    <div key={i} className={`p-2 rounded text-sm ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                      <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{dep.description}</p>
                      {dep.prereqCommand && (
                        <pre className={`mt-1 text-xs p-1 rounded ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                          Check: {dep.prereqCommand}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center space-x-2 pt-2 border-t border-gray-700">
              <button
                onClick={() => setShowExecuteModal(true)}
                className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Execute on Agent
              </button>
              <button
                onClick={() => handleSyncTechnique(test.techniqueId)}
                className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                <FontAwesomeIcon icon={faSync} className="mr-2" />
                Sync from GitHub
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('atomic.title')}
        subtitle={t('atomic.subtitle')}
        icon={<FontAwesomeIcon icon={faAtom} className="text-red-500" />}
      >
        {selectedTests.length > 0 && (
          <button
            onClick={() => setShowExecuteModal(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <FontAwesomeIcon icon={faPlay} className="mr-2" />
            Execute {selectedTests.length} Selected
          </button>
        )}
        {getVariableCount() > 0 && (
          <button
            onClick={() => setShowVariablesModal(true)}
            className={`flex items-center px-4 py-2 rounded-lg ${
              darkMode ? 'bg-purple-700 text-white hover:bg-purple-600' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
            }`}
            title="Manage saved variables"
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            Variables ({getVariableCount()})
          </button>
        )}
        <button
          onClick={() => setShowSyncModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={faSync} className="mr-2" />
          Sync Technique
        </button>
      </PageHeader>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {stats.totalTests}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Tests</div>
          </div>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {stats.uniqueTechniques}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Techniques</div>
          </div>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div className={`text-2xl font-bold text-blue-500`}>
              {stats.byPlatform?.windows || 0}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Windows Tests</div>
          </div>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div className={`text-2xl font-bold text-orange-500`}>
              {stats.byPlatform?.linux || 0}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Linux Tests</div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
          {/* Search */}
          <div className="flex-1 relative">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by technique ID, name, or description..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg ${
              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Filters
            {(platformFilter || executorFilter || tacticFilter) && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                {[platformFilter, executorFilter, tacticFilter].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter dropdowns */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Platform
              </label>
              <select
                value={platformFilter}
                onChange={(e) => {
                  setPlatformFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">All Platforms</option>
                <option value="windows">Windows</option>
                <option value="linux">Linux</option>
                <option value="darwin">macOS</option>
                <option value="iaas:aws">AWS</option>
                <option value="iaas:azure">Azure</option>
                <option value="iaas:gcp">GCP</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Executor
              </label>
              <select
                value={executorFilter}
                onChange={(e) => {
                  setExecutorFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">All Executors</option>
                <option value="powershell">PowerShell</option>
                <option value="cmd">Command Prompt</option>
                <option value="bash">Bash</option>
                <option value="sh">Shell</option>
                <option value="python">Python</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setPlatformFilter('');
                  setExecutorFilter('');
                  setTacticFilter('');
                  setSearchTerm('');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Test list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-blue-500" />
        </div>
      ) : tests.length === 0 ? (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <FontAwesomeIcon icon={faAtom} className="text-4xl mb-4 opacity-50" />
          <p className="text-lg">No atomic tests found</p>
          <p className="text-sm mt-2">Try adjusting your filters or sync techniques from GitHub</p>
        </div>
      ) : (
        <>
          {/* Selection info */}
          {selectedTests.length > 0 && (
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              darkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'
            }`}>
              <span className={darkMode ? 'text-blue-300' : 'text-blue-700'}>
                {selectedTests.length} test(s) selected
              </span>
              <button
                onClick={() => setSelectedTests([])}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* Test cards */}
          <div className="space-y-4">
            {tests.map(test => renderTestCard(test))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className={`px-4 py-2 rounded ${
                  darkMode ? 'bg-gray-700 text-white disabled:bg-gray-800' : 'bg-gray-200 disabled:bg-gray-100'
                } disabled:opacity-50`}
              >
                Previous
              </button>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className={`px-4 py-2 rounded ${
                  darkMode ? 'bg-gray-700 text-white disabled:bg-gray-800' : 'bg-gray-200 disabled:bg-gray-100'
                } disabled:opacity-50`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Execute Modal */}
      {showExecuteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <FontAwesomeIcon icon={faPlay} className="mr-2 text-green-500" />
                  Execute Atomic Test
                </h2>
                <button
                  onClick={() => setShowExecuteModal(false)}
                  className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Tests to execute */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tests to Execute ({selectedTests.length || 1})
                </label>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                  {(selectedTests.length > 0 ? selectedTests : [expandedTest]).map(testId => {
                    const test = tests.find(t => t._id === testId);
                    return test ? (
                      <div key={testId} className="text-sm mb-2 last:mb-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-red-500">{test.techniqueId}</span>
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>- {test.name}</span>
                        </div>
                        {test.platforms && test.platforms.length > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              Required OS:
                            </span>
                            {test.platforms.map(platform => (
                              <span key={platform}>{getPlatformIcon(platform, true)}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Execution mode */}
              {selectedTests.length > 1 && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Execution Mode
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="execMode"
                        value="single"
                        checked={executionMode === 'single'}
                        onChange={(e) => setExecutionMode(e.target.value)}
                        className="mr-2"
                      />
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Execute individually</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="execMode"
                        value="chain"
                        checked={executionMode === 'chain'}
                        onChange={(e) => setExecutionMode(e.target.value)}
                        className="mr-2"
                      />
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Execute as chain</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Agent selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faRobot} className="mr-2 text-blue-500" />
                  Target Agent *
                </label>
                {/* Proactive warning: No compatible agents available */}
                {(() => {
                  const testsToCheck = selectedTests.length > 0 ? selectedTests : [expandedTest];
                  const requiredPlatforms = new Set();
                  testsToCheck.forEach(testId => {
                    const test = tests.find(t => t._id === testId);
                    if (test?.platforms) {
                      test.platforms.forEach(p => requiredPlatforms.add(p.toLowerCase()));
                    }
                  });

                  const compatibleAgents = agents.filter(agent => {
                    const agentPlatform = agent.platform.toLowerCase();
                    return Array.from(requiredPlatforms).some(tp =>
                      tp === agentPlatform ||
                      (tp === 'darwin' && agentPlatform === 'macos') ||
                      (tp === 'macos' && agentPlatform === 'darwin')
                    );
                  });

                  if (agents.length > 0 && compatibleAgents.length === 0 && requiredPlatforms.size > 0) {
                    return (
                      <div className={`mb-3 p-3 rounded-lg border-2 ${
                        darkMode ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-start gap-2">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5 text-lg" />
                          <div>
                            <p className={`text-sm font-semibold ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                              No Compatible Agents Available!
                            </p>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                              This test requires{' '}
                              <span className="inline-flex gap-1 items-center">
                                {Array.from(requiredPlatforms).map(p => (
                                  <span key={p} className="inline-block">{getPlatformIcon(p, true)}</span>
                                ))}
                              </span>
                              {' '}but all your agents are on different platforms.
                            </p>
                            <p className={`text-xs mt-2 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                              Deploy an agent on the required platform to execute this test.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Select an agent...</option>
                  {agents.map(agent => {
                    // Check if agent platform is compatible with selected tests
                    const testsToCheck = selectedTests.length > 0 ? selectedTests : [expandedTest];
                    const isCompatible = testsToCheck.every(testId => {
                      const test = tests.find(t => t._id === testId);
                      if (!test || !test.platforms) return true;
                      const agentPlatform = agent.platform.toLowerCase();
                      const testPlatforms = test.platforms.map(p => p.toLowerCase());
                      return testPlatforms.some(tp =>
                        tp === agentPlatform ||
                        (tp === 'darwin' && agentPlatform === 'macos') ||
                        (tp === 'macos' && agentPlatform === 'darwin')
                      );
                    });

                    const platformName = agent.platform.toLowerCase() === 'windows' ? 'Windows' :
                                       agent.platform.toLowerCase() === 'linux' ? 'Linux' :
                                       agent.platform.toLowerCase() === 'darwin' ? 'macOS' : agent.platform;

                    return (
                      <option
                        key={agent._id}
                        value={agent._id}
                        className={!isCompatible ? 'text-red-500' : ''}
                      >
                        {agent.name} • {platformName} • {agent.status}{!isCompatible ? ' ⚠ INCOMPATIBLE' : ''}
                      </option>
                    );
                  })}
                </select>
                {agents.length === 0 && (
                  <p className="text-sm text-yellow-500 mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    No approved agents available
                  </p>
                )}
                {/* Platform mismatch warning */}
                {selectedAgent && (() => {
                  const agent = agents.find(a => a._id === selectedAgent);
                  const testsToCheck = selectedTests.length > 0 ? selectedTests : [expandedTest];
                  const incompatibleTests = testsToCheck.filter(testId => {
                    const test = tests.find(t => t._id === testId);
                    if (!test || !test.platforms || !agent) return false;
                    const agentPlatform = agent.platform.toLowerCase();
                    const testPlatforms = test.platforms.map(p => p.toLowerCase());
                    // Check if agent platform matches any test platform
                    return !testPlatforms.some(tp =>
                      tp === agentPlatform ||
                      (tp === 'darwin' && agentPlatform === 'macos') ||
                      (tp === 'macos' && agentPlatform === 'darwin')
                    );
                  });

                  if (incompatibleTests.length > 0) {
                    return (
                      <div className={`mt-3 p-3 rounded-lg border-2 ${
                        darkMode ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-start gap-2">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5" />
                          <div>
                            <p className={`text-sm font-semibold ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                              Platform Mismatch Warning!
                            </p>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                              Selected agent runs on <strong>{agent.platform}</strong>, but {incompatibleTests.length === 1 ? 'this test requires' : 'some tests require'} different platforms.
                              The test will likely fail.
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              <span className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Agent OS:</span>
                              {getPlatformIcon(agent.platform, true)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Target selection - multi-select */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FontAwesomeIcon icon={faCrosshairs} className="mr-2 text-red-500" />
                  Targets (optional)
                </label>
                {targets.length > 0 ? (
                  <div className={`max-h-32 overflow-y-auto rounded-lg border p-2 ${
                    darkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}>
                    {targets.map(target => (
                      <label key={target._id} className={`flex items-center p-1 rounded cursor-pointer hover:${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                        <input
                          type="checkbox"
                          checked={selectedTargets.includes(target._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTargets(prev => [...prev, target._id]);
                            } else {
                              setSelectedTargets(prev => prev.filter(id => id !== target._id));
                            }
                          }}
                          className="mr-2 rounded"
                        />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <span className="mr-1">{target.type === 'ip' ? '🌐' : target.type === 'hostname' ? '🖥️' : '🎯'}</span>
                          {target.value}
                          {target.description && <span className={`ml-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({target.description})</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No targets defined {projectId ? 'for this project' : ''}
                  </p>
                )}
                {selectedTargets.length > 0 && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {selectedTargets.length} target(s) selected - will be logged with the action
                  </p>
                )}
              </div>

              {/* Input arguments for single test */}
              {selectedTests.length <= 1 && expandedTest && (
                <div>
                  {(() => {
                    const test = tests.find(t => t._id === expandedTest);
                    if (!test?.inputArguments?.length) return null;

                    // Get stored values merged with defaults
                    const storedVars = getAllVariables();

                    return (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Input Arguments
                          </label>
                          {getVariableCount() > 0 && (
                            <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                              {getVariableCount()} saved variable(s)
                            </span>
                          )}
                        </div>
                        <div className="space-y-3">
                          {test.inputArguments.map(arg => {
                            // Check if we have a saved value for this argument
                            const savedValue = storedVars[arg.name];
                            const hasSavedValue = savedValue !== undefined && savedValue !== arg.default;
                            const currentValue = inputValues[arg.name] ?? savedValue ?? arg.default ?? '';

                            return (
                              <div key={arg.name}>
                                <div className="flex items-center justify-between">
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <code className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>#{'{' + arg.name + '}'}</code>
                                    {arg.description && <span className="ml-2">{arg.description}</span>}
                                  </label>
                                  {hasSavedValue && (
                                    <span className="text-xs text-purple-500" title="Using saved value">
                                      <FontAwesomeIcon icon={faSave} className="mr-1" />
                                      saved
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={currentValue}
                                    onChange={(e) => setInputValues(prev => ({ ...prev, [arg.name]: e.target.value }))}
                                    placeholder={arg.default || 'Enter value...'}
                                    className={`flex-1 px-3 py-2 rounded border ${
                                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                                    } ${hasSavedValue ? 'border-purple-500' : ''}`}
                                  />
                                  {hasSavedValue && (
                                    <button
                                      onClick={() => {
                                        setInputValues(prev => ({ ...prev, [arg.name]: arg.default || '' }));
                                      }}
                                      className="p-2 text-xs text-gray-500 hover:text-red-500"
                                      title="Reset to default"
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                  )}
                                </div>
                                {arg.default && !hasSavedValue && (
                                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Default: {arg.default}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                          Values you enter will be saved for use in future tests
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className={`p-4 border-t flex justify-end space-x-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowExecuteModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleExecute}
                disabled={executeLoading || !selectedAgent}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {executeLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Executing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    Execute
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Technique Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <FontAwesomeIcon icon={faSync} className="mr-2 text-blue-500" />
                  Sync Technique from GitHub
                </h2>
                <button
                  onClick={() => setShowSyncModal(false)}
                  className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-4">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Technique ID (e.g., T1003, T1059.001)
              </label>
              <input
                type="text"
                id="syncTechniqueId"
                placeholder="T1003"
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                This will fetch the latest tests for this technique from the Atomic Red Team GitHub repository.
              </p>
            </div>

            <div className={`p-4 border-t flex justify-end space-x-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowSyncModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const input = document.getElementById('syncTechniqueId');
                  if (input?.value) {
                    handleSyncTechnique(input.value.toUpperCase());
                    setShowSyncModal(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FontAwesomeIcon icon={faSync} className="mr-2" />
                Sync
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variables Management Modal */}
      {showVariablesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-lg w-full max-h-[80vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <FontAwesomeIcon icon={faSave} className="mr-2 text-purple-500" />
                  Saved Variables
                </h2>
                <button
                  onClick={() => setShowVariablesModal(false)}
                  className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[50vh]">
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                These values are automatically saved when you run tests and will be pre-filled in future tests that use the same variables.
              </p>

              {Object.entries(getAllVariables()).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(getAllVariables()).map(([name, value]) => (
                    <div key={name} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                      <div className="flex-1 min-w-0">
                        <code className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>#{'{' + name + '}'}</code>
                        <p className={`text-sm truncate mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{value}</p>
                      </div>
                      <button
                        onClick={() => {
                          const { [name]: removed, ...rest } = getAllVariables();
                          clearVariables();
                          setMultipleVariables(rest);
                        }}
                        className="ml-2 p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                        title="Remove variable"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No saved variables yet. Run a test with input values to save them.
                </p>
              )}
            </div>

            <div className={`p-4 border-t flex justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  if (window.confirm('Clear all saved variables? This cannot be undone.')) {
                    clearVariables();
                    toast.success('All variables cleared');
                  }
                }}
                disabled={getVariableCount() === 0}
                className={`px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-red-100 text-red-700 hover:bg-red-200'
                } disabled:opacity-50`}
              >
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                Clear All
              </button>
              <button
                onClick={() => setShowVariablesModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper component that provides the context
const AtomicTestLibrary = () => {
  const { projectId } = useParams();

  return (
    <AtomicVariablesProvider projectId={projectId}>
      <AtomicTestLibraryInner projectId={projectId} />
    </AtomicVariablesProvider>
  );
};

export default AtomicTestLibrary;
